/**
 * Cookie, privacy and security notices.
 *
 * Deliberately a NOTICE and not a consent banner. This site sets exactly one
 * cookie, it is strictly necessary to keep someone signed in, and there is no
 * analytics, advertising or third-party script anywhere in it. An "Accept
 * cookies" gate would imply a choice that does not exist and consent that is
 * not required — theatre that trains people to dismiss dialogues without
 * reading them. What is owed here is a plain, accurate statement of what is
 * stored and why.
 *
 * Rendered on every screen, signed in or not, so it is reachable before anyone
 * creates an account.
 */
export function SiteNotices() {
  return (
    <footer className="site-notices">
      <p className="muted" style={{ marginTop: 0 }}>
        This site uses <strong>one strictly necessary cookie</strong> to keep you signed in.
        No tracking, no advertising, no third-party scripts.
      </p>

      <details>
        <summary style={{ cursor: 'pointer', minHeight: '2.75rem', display: 'flex', alignItems: 'center' }}>
          Cookies, data and security
        </summary>

        <div style={{ marginTop: '1rem' }}>
          <h2 style={{ fontSize: '1.05rem' }}>What is stored on your device</h2>
          <table className="notice-table">
            <thead>
              <tr><th scope="col">Name</th><th scope="col">Purpose</th><th scope="col">Kept for</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><code>map_session</code><br /><span className="muted">cookie</span></td>
                <td>
                  Keeps you signed in. Set when you sign in or create an account, and deleted when
                  you sign out. It holds a random token only — never your email or password.
                  Marked <code>HttpOnly</code> (scripts on the page cannot read it),
                  <code> Secure</code> (sent over HTTPS only) and <code>SameSite=Lax</code>.
                </td>
                <td>30 days</td>
              </tr>
              <tr>
                <td><code>map.preferences</code><br /><span className="muted">local storage</span></td>
                <td>
                  Your reading and display choices — text size, contrast and reduced motion. This
                  stays on your device and is never sent to the server.
                </td>
                <td>Until you clear it</td>
              </tr>
              <tr>
                <td><code>map.install-dismissed</code><br /><span className="muted">local storage</span></td>
                <td>
                  On a phone or tablet, remembers that you dismissed the suggestion to add Hazelled
                  to your home screen, so it is not shown again for 30 days. Holds only the date.
                  Never sent to the server.
                </td>
                <td>Until you clear it</td>
              </tr>
            </tbody>
          </table>
          <p className="muted">
            That is the complete list. Because the cookie is strictly necessary to provide a service
            you asked for, it does not require consent — and because nothing here tracks you, there
            is nothing to opt out of.
          </p>

          <h2 style={{ fontSize: '1.05rem' }}>How your account is protected</h2>
          <ul>
            <li>
              Passwords are hashed with a per-account salt before storage. Your password is never
              written down in a readable form, and cannot be recovered from the database — only reset.
            </li>
            <li>
              The database stores a <strong>hash</strong> of your session token, not the token
              itself, so a copy of the database does not let anyone sign in as you.
            </li>
            <li>
              Changing your password immediately ends every other signed-in session on your account.
            </li>
            <li>
              Your assessments are yours. Every request is checked against the account that owns it,
              so no other signed-in user can open, answer or view your attempts or results.
            </li>
            <li>
              Administrators of this site can see how much each account uses it: how many tests
              were taken and questions answered, the time spent inside tests (worked out from when
              each test started, was answered and was submitted), when you were last active in a
              test and when you last signed in. They cannot see your scores or answers there,
              nothing beyond those records is collected for it, and every time they look is
              recorded in the audit log.
            </li>
            <li>
              You can delete your account and personal data from your profile at any time. Past
              attempts are detached rather than kept against your name.
            </li>
          </ul>

          <h2 style={{ fontSize: '1.05rem' }}>What we ask of you</h2>
          <ul>
            <li>Use a password you do not use anywhere else, and sign out on shared computers.</li>
            <li>
              Do not share your account. Your progress and the topics you need to work on are
              calculated from your own answers, and become meaningless if someone else uses them.
            </li>
            <li>
              If you believe your account has been accessed by someone else, change your password —
              that alone will sign out every other session.
            </li>
          </ul>

          <h2 style={{ fontSize: '1.05rem' }}>About this practice material</h2>
          <p>
            This is independent practice material. It is <strong>not affiliated with, endorsed by,
            or equivalent to any official examination</strong>, and the questions are original items
            written for this platform. Results are a raw practice score — the number you answered
            correctly out of the number scored. It is not a scaled score and it does not predict the
            outcome of any real examination.
          </p>
        </div>
      </details>
    </footer>
  );
}
